import React, { useEffect, useRef } from 'react';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { motion, AnimatePresence } from 'framer-motion';

// LogoutModal component for displaying a logout confirmation modal
const LogoutModal = ({ isOpen, onClose, onConfirm }) => {
  const modalRef = useRef();

  useEffect(() => {
    // Handle clicks outside the modal to close it
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add or remove the event listener based on the modal's open state
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Cleanup the event listener when the component unmounts or isOpen changes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div 
            className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg border-t-4 border-primary md:max-w-md"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: "tween", ease: "easeInOut", duration: 0.4 }}
            ref={modalRef}
          >
            <div className="flex flex-col items-center">
              <AiOutlineExclamationCircle className="text-red-500 text-6xl mb-4" />
              <h2 className="text-2xl font-semibold mb-4">Are you sure?</h2>
              <p className="text-gray-600 mb-6 text-center">Do you want to logout?</p>
              <div className="flex flex-wrap justify-center space-x-4 space-y-2 md:space-y-0">
                <button 
                  onClick={onConfirm} 
                  className="bg-blue-500 text-white px-6 py-2 rounded-full shadow-md hover:bg-blue-600 transition-all duration-300 transform hover:scale-105"
                >
                  Yes, logout!
                </button>
                <button 
                  onClick={onClose} 
                  className="bg-red-500 text-white px-6 py-2 rounded-full shadow-md hover:bg-red-600 transition-all duration-300 transform hover:scale-105"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LogoutModal;
